import React, { useState, useEffect } from 'react';

const API_BASE = '/api';
const BRAND = '#FF385C';
const COLORS = ['#FF385C', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];

const headers = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const StatCard = ({ label, value, delta }) => (
  <div style={{
    background: '#fff', borderRadius: 12, padding: '1.5rem',
    border: '1px solid #e5e7eb', flex: '1 1 200px', minWidth: 180,
  }}>
    <div style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 500, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
      {label}
    </div>
    <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#111827' }}>{value}</div>
    {delta !== undefined && (
      <div style={{ fontSize: '0.8rem', color: delta >= 0 ? '#10b981' : '#ef4444', marginTop: 4 }}>
        {delta >= 0 ? '+' : ''}{delta} this week
      </div>
    )}
  </div>
);

// Simple CSS bar chart (no library needed)
const SimpleBarChart = ({ data, dataKey, labelKey, color }) => {
  const maxVal = Math.max(...data.map((d) => d[dataKey]), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {data.map((item, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 80, fontSize: '0.8rem', color: '#4b5563', textAlign: 'right', flexShrink: 0 }}>
            {item[labelKey]}
          </div>
          <div style={{ flex: 1, background: '#f3f4f6', borderRadius: 4, height: 24, overflow: 'hidden' }}>
            <div style={{
              width: `${(item[dataKey] / maxVal) * 100}%`,
              height: '100%',
              background: color || BRAND,
              borderRadius: 4,
              minWidth: item[dataKey] > 0 ? 24 : 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              paddingRight: 8,
              transition: 'width 0.3s ease',
            }}>
              <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#fff' }}>{item[dataKey]}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Simple sparkline using SVG
const Sparkline = ({ data, width = 400, height = 120, color }) => {
  if (!data || data.length === 0) return null;
  const maxVal = Math.max(...data.map((d) => d.count), 1);
  const padding = 4;
  const w = width - padding * 2;
  const h = height - padding * 2;

  const points = data.map((d, i) => {
    const x = padding + (i / (data.length - 1 || 1)) * w;
    const y = padding + h - (d.count / maxVal) * h;
    return `${x},${y}`;
  }).join(' ');

  // Area fill
  const areaPoints = `${padding},${padding + h} ${points} ${padding + w},${padding + h}`;

  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      <polygon points={areaPoints} fill={color || BRAND} opacity="0.1" />
      <polyline points={points} fill="none" stroke={color || BRAND} strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
};

// Simple donut using SVG
const SimpleDonut = ({ data, dataKey, labelKey }) => {
  const total = data.reduce((sum, d) => sum + d[dataKey], 0);
  if (total === 0) return <div style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>No data</div>;

  const size = 160;
  const cx = size / 2;
  const cy = size / 2;
  const r = 60;
  const inner = 35;
  let cumAngle = -90;

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {data.map((item, i) => {
          const pct = item[dataKey] / total;
          const angle = pct * 360;
          const startAngle = cumAngle;
          cumAngle += angle;

          const startRad = (startAngle * Math.PI) / 180;
          const endRad = ((startAngle + angle) * Math.PI) / 180;
          const largeArc = angle > 180 ? 1 : 0;

          const x1 = cx + r * Math.cos(startRad);
          const y1 = cy + r * Math.sin(startRad);
          const x2 = cx + r * Math.cos(endRad);
          const y2 = cy + r * Math.sin(endRad);
          const ix1 = cx + inner * Math.cos(endRad);
          const iy1 = cy + inner * Math.sin(endRad);
          const ix2 = cx + inner * Math.cos(startRad);
          const iy2 = cy + inner * Math.sin(startRad);

          const d = `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} L ${ix1} ${iy1} A ${inner} ${inner} 0 ${largeArc} 0 ${ix2} ${iy2} Z`;

          return <path key={i} d={d} fill={COLORS[i % COLORS.length]} />;
        })}
        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" style={{ fontSize: 20, fontWeight: 700, fill: '#111827' }}>
          {total}
        </text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {data.map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8rem' }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: COLORS[i % COLORS.length] }} />
            <span style={{ color: '#4b5563' }}>{item[labelKey]}</span>
            <span style={{ fontWeight: 600, color: '#111827' }}>{item[dataKey]}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const AdminOverview = () => {
  const [stats, setStats] = useState(null);
  const [timeseries, setTimeseries] = useState([]);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([
      fetch(`${API_BASE}/admin/stats/overview`, { headers: headers() }).then((r) => r.json()),
      fetch(`${API_BASE}/admin/stats/timeseries?metric=stores&days=30`, { headers: headers() }).then((r) => r.json()),
      fetch(`${API_BASE}/admin/activity/recent`, { headers: headers() }).then((r) => r.json()),
    ])
      .then(([statsData, tsData, actData]) => {
        setStats(statsData);
        setTimeseries(
          (tsData.data || []).map((d) => ({
            date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            count: d.count,
          }))
        );
        setActivity(actData);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError('Failed to load dashboard data');
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '4rem' }}>
        <div className="spinner-border" role="status" style={{ color: BRAND }}>
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '1rem 1.25rem', color: '#991b1b' }}>
        {error}
      </div>
    );
  }

  return (
    <div>
      <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', marginBottom: '1.5rem' }}>Dashboard Overview</h2>

      {/* Stat cards */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '2rem' }}>
        <StatCard label="Total Stores" value={stats.total_stores} delta={stats.new_stores_last_7_days} />
        <StatCard label="Total Users" value={stats.total_users} delta={stats.new_users_last_7_days} />
        <StatCard label="Total Products" value={stats.total_products} />
        <StatCard label="New This Week" value={stats.new_stores_last_7_days + stats.new_users_last_7_days} />
      </div>

      {/* Charts row */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '2rem' }}>
        {/* Sparkline — new stores over 30 days */}
        <div style={{
          flex: '2 1 400px', background: '#fff', borderRadius: 12, padding: '1.5rem',
          border: '1px solid #e5e7eb', minWidth: 0,
        }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#374151', marginBottom: '1rem' }}>New Stores (30 days)</h3>
          <Sparkline data={timeseries} height={140} />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#9ca3af', marginTop: 4 }}>
            <span>{timeseries[0]?.date}</span>
            <span>{timeseries[timeseries.length - 1]?.date}</span>
          </div>
        </div>

        {/* Bar chart — stores by city */}
        <div style={{
          flex: '1 1 280px', background: '#fff', borderRadius: 12, padding: '1.5rem',
          border: '1px solid #e5e7eb', minWidth: 0,
        }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#374151', marginBottom: '1rem' }}>Stores by City</h3>
          <SimpleBarChart data={stats.stores_by_city} dataKey="count" labelKey="city" />
        </div>
      </div>

      {/* Pie + activity row */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        {/* Donut — products by type */}
        <div style={{
          flex: '1 1 280px', background: '#fff', borderRadius: 12, padding: '1.5rem',
          border: '1px solid #e5e7eb',
        }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#374151', marginBottom: '1rem' }}>Products by Type</h3>
          <SimpleDonut data={stats.products_by_item_type} dataKey="count" labelKey="item_type" />
        </div>

        {/* Recent activity */}
        <div style={{
          flex: '2 1 400px', background: '#fff', borderRadius: 12, padding: '1.5rem',
          border: '1px solid #e5e7eb',
        }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#374151', marginBottom: '1rem' }}>Recent Activity</h3>
          <div style={{ maxHeight: 280, overflowY: 'auto' }}>
            {activity && [...(activity.recent_stores || []).map((s) => ({
              type: 'store',
              text: `New store: ${s.name} (${s.city})`,
              sub: s.owner_email,
              time: s.created_at,
            })), ...(activity.recent_users || []).map((u) => ({
              type: 'user',
              text: `New user: ${u.email}`,
              sub: u.role,
              time: u.created_at,
            }))]
              .sort((a, b) => new Date(b.time) - new Date(a.time))
              .slice(0, 12)
              .map((item, i) => (
                <div key={i} style={{
                  padding: '0.6rem 0',
                  borderBottom: '1px solid #f3f4f6',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}>
                  <div>
                    <div style={{ fontSize: '0.85rem', color: '#1f2937' }}>
                      <i className={`bi ${item.type === 'store' ? 'bi-shop' : 'bi-person'} me-2`}
                        style={{ color: item.type === 'store' ? BRAND : '#3b82f6' }} />
                      {item.text}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{item.sub}</div>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                    {new Date(item.time).toLocaleDateString()}
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOverview;
